import { Category } from "../../types";
import categoryIconCoverImg from "../../assets/images/category-icon-cover.png";
import categoryIconCoverSelectedImg from "../../assets/images/category-icon-cover-selected.png";
import arrowRightImage from "../../assets/images/arrow-right.png";
import clickSound from "../../../../assets/audio/click.wav";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";

interface Props {
  className?: string;
  category: Category;

  // UI & style
  label?: string;
  selected?: boolean;
  disabled?: boolean; // disables click & hover
  showArrow?: boolean; // toggle arrow icon
  showLabel?: boolean; // toggle top label

  // Events
  onClick?: (category: Category) => void;
  onHover?: (category: Category) => void;
  onSelectChange?: (category: Category, selected: boolean) => void;
}

const CategoryCard = ({
  className = "",
  category,
  selected,
  disabled,
  label = "Category",
  showArrow = true,
  showLabel = true,
  onClick,
  onHover,
  onSelectChange,
}: Props) => {
  const { playEffect } = useAudioEffects();

  const handleClick = () => {
    if (disabled) return;
    playEffect(clickSound);
    onClick?.(category);
    onSelectChange?.(category, !selected);
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={() => !disabled && onHover?.(category)}
      className={`flex items-center cursor-pointer transition 
        hover:opacity-80 active:scale-95 group 
        ${disabled ? "opacity-50 pointer-events-none" : ""} 
        ${className}`}
    >
      {/* Category Icon with background */}
      <div className="w-[130px] h-[130px] relative flex items-center justify-center">
        <img
          src={selected ? categoryIconCoverSelectedImg : categoryIconCoverImg}
          alt="cover"
          className="absolute top-[35%] left-[50%] translate-x-[-50%] translate-y-[-50%] 
            min-w-[175%] min-h-[135%] z-[-1]"
        />
        <img
          src={category.iconSrc}
          alt="icon"
          className="max-w-[65px] 1920px:max-w-[90px]"
        />
      </div>

      {/* Info panel */}
      <div
        className={`relative mt-[-5px] ml-[-30px] pl-[47px] p-2 pr-4 rounded-md 
          bg-gradient-to-r overflow-hidden w-full min-w-[220px] 1920px:min-w-[260px] min-h-[80px] 
          flex items-center justify-between
          ${
            selected
              ? "from-transparent to-[#42296e]/80"
              : "from-transparent to-white/25"
          }`}
      >
        {/* Top & bottom borders */}
        <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-white/5 to-white/25" />
        <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-white/5 to-white/25" />

        {/* Text info */}
        <div>
          {showLabel && (
            <span className="text-white/50 text-[14px] 3440px:text-[18px]">
              {label}
            </span>
          )}
          <p className="text-white text-[16px] leading-4 text-wrap 3440px:text-[24px]">
            {category?.name}
          </p>
        </div>

        {/* Arrow (optional) */}
        {showArrow && (
          <div
            className="border-[1px] transition-all duration-500 ease-in-out 
              border-white/20 rounded-sm h-full p-2 bg-white/10 min-h-[50px] 
              flex justify-center items-center cursor-pointer 
              group-hover:bg-[#43276d]/50"
          >
            <img src={arrowRightImage} className="w-[7px]" alt="arrow" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryCard;
